import { DEFAULT_LAT, DEFAULT_LNG } from "@/constants";
import { cn } from "@/lib/utils";
import { parseNumber } from "@/lib/utils/";
import {
  Map as GoogleMap,
  MapCameraChangedEvent,
  MapEvent,
  MapMouseEvent,
  Marker,
} from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

interface Location {
  lat: number;
  lng: number;
}

interface MapProps {
  className?: string;
  zoom?: number;
  onLocationChange: (lat: number, lng: number) => void;
}

const defaultCenter = {
  lat: DEFAULT_LAT,
  lng: DEFAULT_LNG,
};

export default function Map({
  className,
  zoom = 11,
  onLocationChange,
}: MapProps) {
  const form = useFormContext();

  const lat = form.watch("latitude");
  const lng = form.watch("longitude");

  const [camera, setCamera] = useState<Location | undefined>();

  const handleCameraChange = useCallback((event: MapCameraChangedEvent) => {
    setCamera(event.detail.center);
  }, []);

  useEffect(() => {
    const latParsed = parseNumber(lat);
    const lngParsed = parseNumber(lng);
    if (latParsed && lngParsed) {
      setCamera({ lat: latParsed, lng: lngParsed });
    }
  }, [lat, lng]);

  const onMarkerDragEnd = useCallback(
    (event: MapMouseEvent["detail"]) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      onLocationChange(lat, lng);
    },
    [onLocationChange],
  );

  const onMapDragEnd = useCallback(
    (event: MapEvent) => {
      const center = event.map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      onLocationChange(lat, lng);
    },
    [onLocationChange],
  );

  return (
    <div
      className={cn("bg-slate-100 w-full h-full min-h-80 min-w-80", className)}
    >
      <GoogleMap
        defaultZoom={zoom}
        defaultCenter={defaultCenter}
        center={camera || defaultCenter}
        onCameraChanged={handleCameraChange}
        onDragend={onMapDragEnd}
      >
        <Marker
          draggable={true}
          position={camera || defaultCenter}
          onDragEnd={onMarkerDragEnd}
        />
      </GoogleMap>
    </div>
  );
}
